import React, {Component} from 'react';
import {ModalBody} from 'reactstrap';
import {toast} from 'react-toastify';
import * as Sentry from "@sentry/react";
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from "../../../settings/local_settings";
import {Spinner} from "../../Spinner";
import {CSVFileInput} from "../../CSVFileInput";
import {COLUMN_CLIENT_NAME, COLUMNS, MANDATORY_COLUMNS} from "./columns";
import {ColumnMappingConfiguration} from "./ColumnMappingConfiguration";
import {FaIcon} from "../../FaIcon";
import {DateFormatSelect} from "../../elements/DateFormatSelect";
import {CurrencySelect} from "../../elements/CurrencySelect";
import {TimezoneSelect} from "../../selects/TimezoneSelect";
import {HELP_SHEET, LinkToArticle} from "../../help_center/LinkToArticle";
import {withTranslation} from 'react-i18next';
import {
    describeIssue,
    groupIssuesByColumn,
    MAX_INLINE_ISSUES,
    parseSheetIssues,
    resolveColumnLabel,
    suggestionForIssue,
    summarizeRows,
    UNKNOWN_COLUMN
} from "./sheetIssues";
import {parseDateFormatInference, readSampleAs} from "./dateFormatInference";

export const MODE_CREATE = "create";
export const MODE_EDIT = "edit";

// Un toast con un id YA ACTIVO se descarta en silencio (ver CLAUDE.md): update si está vivo.
const SUBMIT_ERROR_TOAST_ID = 'sheet-account-submit-error';

// El backend rechaza el alta cuando la planilla tiene más filas de las que el sync puede leer.
// `status` viaja tal cual desde `SheetAPIClient.ERROR_TOO_MANY_ROWS`.
const STATUS_TOO_MANY_ROWS = 'SHEET_TOO_MANY_ROWS';


class AddAdAccountFormComponent extends Component {

    constructor(props) {
        super(props);

        const isEdit = this.isEditMode();
        const initial = this.props.initialSheetAccount || {};
        // El mapeo YA RESUELTO contra el archivo de hoy, cuando el backend lo manda. Importa que
        // gane sobre `column_mapping`: el backend resuelve un header guardado contra el archivo
        // de forma tolerante (espacios, mayúsculas, formas de la ñ, mojibake), y si acá usáramos
        // el nombre CRUDO la pantalla marcaría como rotas justo las columnas que el sync lee
        // perfecto — con el desplegable vacío y el guardado bloqueado. El matcher es uno solo y
        // vive en el backend; esto es el resultado de haberlo corrido.
        // Un backend sin deployar no manda la clave y se cae al comportamiento de siempre.
        const initialMapping = this.props.resolvedColumnMapping || initial.column_mapping || {};
        const initialFilters = initial.filters || {};
        const initialClientFilter = this.extractClientNameFilter(initialFilters);

        // FASE 5 — el diagnóstico de "Arreglar conexión" ahora también valida CONTENIDO y puede traer
        // el mismo `sheet_issues` que devuelve el POST. Es el MISMO dato, así que se dibuja igual:
        // se siembra `columnIssues` y las filas del paso 3 lo pintan solas, con su botón de
        // corrección incluido. `EditSheetAdAccountModal` ya resolvió el fetch antes de montarnos.
        // ⚠️ Acá `sheet_issues` viene como OBJETO (en el POST viene como string JSON dentro de
        // `errors`); `parseSheetIssues` acepta las dos formas y devuelve null si no hay nada.
        const diagnosticIssues = parseSheetIssues(this.props.diagnostic);
        const hasDiagnosticIssues = !!diagnosticIssues && diagnosticIssues.issues.length > 0;
        const initialDateFormat = isEdit ? (initial.date_format || null) : null;

        this.state = {
            isSaving: false,
            accountName: isEdit ? (initial.ad_account_name || "") : "",
            accountId: isEdit ? (initial.ad_account_id ? String(initial.ad_account_id) : "") : "",
            csvUrl: isEdit ? (initial.url || null) : null,
            dateFormat: isEdit ? (initial.date_format || null) : null,
            currency: isEdit ? (initial.currency || null) : null,
            csvHeaders: isEdit ? (this.props.currentCsvHeaders || null) : null,
            // Filas de muestra del archivo que el usuario importó en ESTA sesión del modal. Le
            // ganan a `props.sampleRows` (que describe la URL guardada) apenas se importa otra.
            // El flag va aparte del valor a propósito: "importé y no vinieron filas" y "todavía
            // no importé nada" son distintos, y colapsarlos en un `null` hace que el preview
            // caiga a la muestra vieja justo en el caso que este arreglo vino a cerrar.
            hasImportedNewFile: false,
            importedSampleRows: null,
            timezone: isEdit ? (initial.timezone || null) : null,
            columnMappings: COLUMNS.map(column => ({
                column: column,
                header: isEdit ? (initialMapping[column] || null) : null
            })),
            clientNameFilter: initialClientFilter,
            // `{sample, candidates[]}` cuando el backend no pudo decidir el formato de fecha porque
            // más de uno explica el archivo. null = no hay nada que preguntar (ver
            // `dateFormatInference.js`). En el alta arranca en null: todavía no se importó nada.
            dateFormatInference: null,
            // Error del último intento de guardado: {title, items[], notes[]}. null = nada que decir.
            submitError: null,
            // Detalle por celda que devolvió el backend, agrupado por columna del CSV. Se ancla a la
            // fila del mapeo correspondiente en el paso 3. Puede venir del POST o del diagnóstico.
            columnIssues: hasDiagnosticIssues ? groupIssuesByColumn(diagnosticIssues.issues) : {},
            // Resumen (filas afectadas, escaneo, límites) del detalle que trajo el DIAGNÓSTICO, para
            // el banner de "Arreglar conexión". Se vacía apenas el usuario toca algo: describe un
            // formulario que ya no es el que está viendo.
            diagnosticIssuesSummary: hasDiagnosticIssues
                ? this.buildIssuesSummary(diagnosticIssues, {mappedColumns: COLUMNS, dateFormat: initialDateFormat})
                : null
        };

        this.handleColumnMappingChange = this.handleColumnMappingChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCSVFileChange = this.handleCSVFileChange.bind(this);
        this.handleDateFormatChange = this.handleDateFormatChange.bind(this);
        this.handleClientNameFilterChange = this.handleClientNameFilterChange.bind(this);
        this.handleTimezoneChange = this.handleTimezoneChange.bind(this);
        this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
        this.handleAccountNameChange = this.handleAccountNameChange.bind(this);
        this.handleAccountIdChange = this.handleAccountIdChange.bind(this);
        this.handleApplySuggestion = this.handleApplySuggestion.bind(this);
        this.handleApplyHeaderSuggestion = this.handleApplyHeaderSuggestion.bind(this);
        this.handleApplyAllRemapping = this.handleApplyAllRemapping.bind(this);
    }

    isEditMode() {
        return this.props.mode === MODE_EDIT;
    }

    extractClientNameFilter(filters) {
        if (!filters || !Array.isArray(filters.conditions)) return "";
        const cond = filters.conditions.find(c => c.column === "CLIENT_NAME" && c.operator === "equals");
        return cond ? (cond.value || "") : "";
    }

    handleClientNameFilterChange(event) {
        this.setState({clientNameFilter: event.target.value});
    }

    handleAccountNameChange(event) {
        this.setState({accountName: event.target.value});
    }

    handleAccountIdChange(event) {
        this.setState({accountId: event.target.value});
    }

    getColumnMappingForPost() {
        if (this.state.columnMappings === null) return null;
        const columnsMapping = {};
        this.state.columnMappings.forEach((m) => {
            columnsMapping[m.column] = m.header;
        });
        return columnsMapping;
    }

    getFiltersForPost() {
        const conditions = this.state.clientNameFilter ? [{
            "column": "CLIENT_NAME",
            "operator": "equals",
            "value": this.state.clientNameFilter
        }] : [];
        return {"filterType": "AND", "conditions": conditions};
    }

    handleDateFormatChange(newFormatValue) { this.setState({dateFormat: newFormatValue}); }
    handleTimezoneChange(newTimezone) { this.setState({timezone: newTimezone}); }
    handleCurrencyChange(event) { this.setState({currency: event.target.value}); }

    hasDuplicatedHeaders() {
        if (this.state.columnMappings === null) return false;
        const headerUsed = [];
        let hasDuplication = false;
        this.state.columnMappings.forEach((m) => {
            if (m.header !== null) {
                if (headerUsed.includes(m.header)) hasDuplication = true;
                else headerUsed.push(m.header);
            }
        });
        return hasDuplication;
    }

    /**
     * Todo lo que impide guardar, NOMBRADO. Antes esto era un booleano (`isFormValid`) que apagaba
     * el botón sin decir qué faltaba: el click ni siquiera llegaba a `handleSubmit`.
     */
    getBlockingIssues() {
        const {t} = this.props;
        const blockers = [];

        if (!this.isCSVFileImported()) blockers.push(t('sheet_account.missing.csv'));
        if (!this.state.accountName) blockers.push(t('sheet_account.missing.name'));
        if (!this.state.dateFormat) blockers.push(t('sheet_account.missing.date_format'));
        if (!this.state.currency) blockers.push(t('sheet_account.missing.currency'));
        if (!this.state.timezone) blockers.push(t('sheet_account.missing.timezone'));

        this.state.columnMappings.forEach((m) => {
            const label = resolveColumnLabel(m.column, t);
            if (MANDATORY_COLUMNS.includes(m.column) && !m.header) {
                blockers.push(t('sheet_account.missing.mapping', {
                    column: label, interpolation: {escapeValue: false}
                }));
            }
            if (this.isHeaderMissing(m.header)) {
                blockers.push(t('sheet_account.missing.orphan_mapping', {
                    column: label, header: m.header, interpolation: {escapeValue: false}
                }));
            }
        });

        if (this.isClientColumnInCSV() && !this.state.clientNameFilter) {
            blockers.push(t('sheet_account.missing.client_filter'));
        }
        if (this.hasDuplicatedHeaders()) blockers.push(t('sheet_account.missing.duplicated_headers'));

        return blockers;
    }

    notifySubmitError(message) {
        if (!message) return;
        if (toast.isActive(SUBMIT_ERROR_TOAST_ID)) {
            toast.update(SUBMIT_ERROR_TOAST_ID, {render: message, type: 'error'});
        } else {
            toast.error(message, {toastId: SUBMIT_ERROR_TOAST_ID});
        }
    }

    showSubmitError({title, items, notes}) {
        this.setState({submitError: {title: title, items: items || [], notes: notes || []}});
        this.notifySubmitError(title);
    }

    handleSubmit(event) {
        event.preventDefault();
        if (this.state.isSaving) return;

        const blockers = this.getBlockingIssues();
        if (blockers.length > 0) {
            const {t} = this.props;
            this.showSubmitError({
                title: this.isEditMode()
                    ? t('sheet_account.errors.missing_title_edit')
                    : t('sheet_account.errors.missing_title'),
                items: blockers
            });
            return;
        }

        this.setState({isSaving: true, submitError: null, columnIssues: {}, diagnosticIssuesSummary: null});

        const method = this.isEditMode() ? 'PATCH' : 'POST';
        const payload = this.isEditMode() ? {
            'url': this.state.csvUrl,
            'columns_mapping': this.getColumnMappingForPost(),
            'filters': this.getFiltersForPost(),
            'date_format': this.state.dateFormat
        } : {
            'url': this.state.csvUrl,
            'columns_mapping': this.getColumnMappingForPost(),
            'filters': this.getFiltersForPost(),
            'name': this.state.accountName,
            'date_format': this.state.dateFormat,
            'currency': this.state.currency,
            'timezone': this.state.timezone,
            'account_id': this.state.accountId
        };

        // ⚠️ El `.catch()` NO es decorativo: `APIClient` nunca rejecta, así que lo único que puede
        // caer acá es una excepción de NUESTRO código dentro del `.then()`. Sin él, esa excepción
        // es una promise rejection sin manejar y el usuario ve "no pasa nada" (bug 2026-09-01).
        new APIClient(this.submitUrl()).call(method, payload)
            .then((data) => this.handleSubmitResponse(data))
            .catch((error) => this.handleSubmitCrash(error));
    }

    handleSubmitResponse(data) {
        const {t} = this.props;
        this.setState({isSaving: false});

        if (data && data.status === 'OK') {
            // ANTES del callback: `afterSuccessfulSubmit` cierra el modal y desmonta este componente.
            this.notifyPartialCheck(data.data);
            this.props.afterSuccessfulSubmit(data.data);
            return;
        }

        const status = data ? data.status : null;

        if (status === 'METRIC_DATA_NOT_VALID') {
            this.showMetricDataError(data);
            return;
        }

        if (status === STATUS_TOO_MANY_ROWS) {
            this.showTooManyRowsError(data);
            return;
        }

        if (status === 'NETWORK_ERROR') {
            this.showSubmitError({title: t('sheet_account.errors.network')});
            return;
        }

        // No se muestra el `error_message` crudo del backend: puede filtrar info de otra agencia
        // (regla de aislamiento cross-agency). El código sí, para que soporte pueda seguirlo.
        const code = data && (data.error_code || data.status);
        this.showSubmitError({
            title: t('sheet_account.errors.generic'),
            notes: code ? [t('sheet_account.errors.generic_code', {
                code: String(code), interpolation: {escapeValue: false}
            })] : []
        });
    }

    /**
     * El éxito puede traer letra chica: si la validación interactiva miró sólo una PARTE del archivo,
     * el backend lo dice con `sheet_issues.truncated` (`extra_data_for_partial_check`). La cuenta se
     * creó igual — no es un error — pero puede haber filas con problemas más abajo, y callarlo es
     * exactamente el límite silencioso del que viene esta feature: una planilla aprobada en el alta
     * que a la noche el sync no puede leer.
     *
     * `toast.info` y no el bloque de error de la pantalla: el modal se cierra en la línea siguiente,
     * así que un aviso anclado al formulario se iría con él. Sin `toastId` a propósito — dos altas
     * seguidas con archivos grandes tienen que avisar dos veces, y un id ya activo se descarta EN
     * SILENCIO (gotcha de `react-toastify` documentado en CLAUDE.md).
     *
     * ⚠️ Acá `sheet_issues` viene como OBJETO (en el rechazo viaja como string JSON dentro de
     * `errors`); `parseSheetIssues` acepta las dos formas y devuelve null si no hay nada, así que un
     * backend sin deployar deja el alta exactamente como estaba.
     */
    notifyPartialCheck(payload) {
        const {t} = this.props;
        const parsed = parseSheetIssues(payload);
        if (!parsed || !parsed.truncated) return;
        toast.info(t('sheet_account.issues.truncated'));
    }

    /**
     * La planilla tiene más filas de las que el sync puede procesar: la cuenta NO se creó.
     *
     * Tiene copy propio porque el genérico ("intentar de nuevo en unos minutos; si el problema
     * persiste, contactar al equipo") es una instrucción FALSA para este caso: el mismo archivo va a
     * dar el mismo resultado siempre, y lo que hay que hacer es filtrarlo o partirlo. Cuando el
     * backend manda los números (`rows_in_file` / `max_rows_supported`) se dicen, que es la
     * diferencia entre "es muy grande" y "tiene 240.000 filas y leemos hasta 100.000".
     */
    showTooManyRowsError(data) {
        const {t} = this.props;
        const parsed = parseSheetIssues(data && data.errors);
        const rowsInFile = parsed ? parsed.rowsInFile : null;
        const maxRows = parsed ? parsed.maxRowsSupported : null;

        this.setState({columnIssues: {}, diagnosticIssuesSummary: null});
        this.showSubmitError({
            title: (rowsInFile !== null && maxRows !== null)
                ? t('sheet_account.errors.too_many_rows_detail', {
                    rows: Number(rowsInFile).toLocaleString('en-US'),
                    max_rows: Number(maxRows).toLocaleString('en-US'),
                    interpolation: {escapeValue: false}
                })
                : t('sheet_account.errors.too_many_rows')
        });
    }

    handleSubmitCrash(error) {
        const {t} = this.props;
        Sentry.captureException(error, {
            extra: {
                scope: 'sheet_add_ad_account_submit',
                mode: this.isEditMode() ? MODE_EDIT : MODE_CREATE,
                url: this.submitUrl()
            }
        });
        this.setState({isSaving: false});
        this.showSubmitError({title: t('sheet_account.errors.unexpected')});
    }

    /**
     * Resumen legible de un `sheet_issues` ya parseado: `{items, notes}`.
     *
     * ES EL ÚNICO LUGAR donde se arma. Lo usan las DOS entradas del mismo dato — el rechazo del POST
     * (`METRIC_DATA_NOT_VALID`) y el diagnóstico de "Arreglar conexión" (`ROW_DATA_NOT_VALID`) —
     * porque son el mismo dato y tienen que verse igual. No lee `this.state`: el constructor lo
     * necesita antes de que exista.
     */
    buildIssuesSummary(parsed, {mappedColumns, dateFormat}) {
        const {t} = this.props;
        const grouped = groupIssuesByColumn(parsed.issues);
        const items = [];
        const notes = [];

        Object.keys(grouped).forEach((column) => {
            const issues = grouped[column];
            const rows = summarizeRows(issues);
            const label = resolveColumnLabel(column, t);

            if (column !== UNKNOWN_COLUMN && mappedColumns.indexOf(column) !== -1) {
                // La fila del mapeo ya muestra el detalle: acá va sólo el resumen.
                items.push(t('sheet_account.issues.rows_with_problems', {
                    count: rows.count, column: label, rows: rows.text,
                    interpolation: {escapeValue: false}
                }));
                return;
            }

            // Columna que el paso 3 no muestra (ej. ADGROUP_NAME): el detalle va en el resumen o se
            // perdería.
            issues.slice(0, MAX_INLINE_ISSUES).forEach((issue) => {
                items.push(describeIssue(issue, {t, dateFormat}));
            });
            if (issues.length > MAX_INLINE_ISSUES) {
                items.push(t('sheet_account.issues.more', {
                    count: issues.length - MAX_INLINE_ISSUES
                }));
            }
        });

        if (parsed.rowsScanned !== null) {
            notes.push(t('sheet_account.issues.scan_summary', {
                rows_scanned: parsed.rowsScanned,
                rows_ok: parsed.rowsOk !== null ? parsed.rowsOk : 0,
                interpolation: {escapeValue: false}
            }));
        }
        // Un límite silencioso es lo que nos trajo hasta acá.
        if (parsed.truncated) notes.push(t('sheet_account.issues.truncated'));
        if (parsed.issuesCapped) notes.push(t('sheet_account.issues.capped'));

        return {items, notes};
    }

    /**
     * `errors` es un STRING con JSON adentro y el backend puede no estar deployado todavía: si no
     * hay detalle parseable caemos al mensaje de siempre, apoyado en `error_message`.
     */
    showMetricDataError(data) {
        const {t} = this.props;
        const columnLabel = resolveColumnLabel(data.error_message, t);
        const parsed = parseSheetIssues(data.errors);

        if (!parsed || parsed.issues.length === 0) {
            this.setState({columnIssues: {}, diagnosticIssuesSummary: null});
            this.showSubmitError({
                title: t('sheet_account.errors.metric_data_not_valid', {
                    column: columnLabel, interpolation: {escapeValue: false}
                })
            });
            return;
        }

        const {items, notes} = this.buildIssuesSummary(parsed, {
            mappedColumns: this.state.columnMappings.map(m => m.column),
            dateFormat: this.state.dateFormat
        });

        // El detalle del POST reemplaza al del diagnóstico: es más nuevo y describe este intento.
        this.setState({columnIssues: groupIssuesByColumn(parsed.issues), diagnosticIssuesSummary: null});
        this.showSubmitError({
            title: t('sheet_account.issues.title', {
                column: columnLabel, interpolation: {escapeValue: false}
            }),
            items: items,
            notes: notes
        });
    }

    /** Corrección en un click: setea el campo sugerido y deja el formulario listo para reintentar. */
    handleApplySuggestion(suggestion) {
        const {t} = this.props;
        if (!suggestion) return;

        if (suggestion.type === 'DATE_FORMAT') {
            this.setState({dateFormat: suggestion.value, submitError: null, columnIssues: {}, diagnosticIssuesSummary: null});
            toast.dismiss(SUBMIT_ERROR_TOAST_ID);
            toast.success(t('sheet_account.issues.suggestion_applied', {
                format: suggestion.value, interpolation: {escapeValue: false}
            }));
        }
    }

    /**
     * Las propuestas de re-mapeo que el backend pudo resolver SIN dudar, y que todavía no están
     * aplicadas. Las que vienen sin `header` (empate que no se pudo desempatar) no entran acá:
     * ésas se preguntan fila por fila, no se aplican de una.
     */
    pendingRemapping() {
        const suggestions = (this.props.diagnostic && this.props.diagnostic.remapping_suggestions) || {};
        const headerByColumn = {};
        this.state.columnMappings.forEach((m) => {
            const proposal = suggestions[m.column];
            if (proposal && proposal.header && proposal.header !== m.header) {
                headerByColumn[m.column] = proposal.header;
            }
        });
        return headerByColumn;
    }

    /**
     * Acepta TODAS las propuestas de una. Es el botón que convierte "arreglá seis desplegables a
     * mano" en un click — el caso que originó esto tenía seis columnas rotas de golpe porque la
     * planilla había cambiado de formato de export entero.
     *
     * No guarda: deja el formulario cargado para que el usuario revise y apriete Guardar. Aplicar
     * y guardar en el mismo gesto le sacaría la última oportunidad de ver que algo quedó mal.
     */
    handleApplyAllRemapping() {
        const {t} = this.props;
        const headerByColumn = this.pendingRemapping();
        const applied = Object.keys(headerByColumn).length;
        if (applied === 0) return;

        this.setState((state) => ({
            columnMappings: state.columnMappings.map((m) => (
                headerByColumn[m.column] ? {...m, header: headerByColumn[m.column]} : m
            )),
            submitError: null,
            columnIssues: {},
            diagnosticIssuesSummary: null
        }));
        toast.dismiss(SUBMIT_ERROR_TOAST_ID);
        toast.success(t('sheet_account.remap.applied', {count: applied}));
    }

    /** Acepta UNA propuesta: la de una fila puntual, o uno de los candidatos de un empate. */
    handleApplyHeaderSuggestion(column, header) {
        if (!header) return;
        this.setState((state) => ({
            columnMappings: state.columnMappings.map((m) => (m.column === column ? {...m, header: header} : m)),
            submitError: null,
            columnIssues: {},
            diagnosticIssuesSummary: null
        }));
    }

    /**
     * El primer valor NO VACÍO que trae esa columna en las filas de muestra, o null.
     *
     * Se saltea los vacíos a propósito: una columna cuya primera fila está en blanco mostraría
     * un preview vacío, que es justo lo que no distingue una columna buena de una equivocada.
     */
    previewFor(header) {
        const headers = this.state.csvHeaders;
        // Apenas hubo un import, las del diagnóstico describen OTRO archivo: `csvHeaders` ya es
        // el nuevo y las dos se cruzan por posición, así que mezclarlas muestra el valor de una
        // columna que no es. Sin muestra nueva se prefiere no mostrar nada.
        const rows = this.state.hasImportedNewFile ? this.state.importedSampleRows : this.props.sampleRows;
        if (!header || !headers || !Array.isArray(rows)) return null;

        const position = headers.indexOf(header);
        if (position < 0) return null;

        for (const row of rows) {
            const value = Array.isArray(row) ? row[position] : null;
            if (value !== undefined && value !== null && String(value).trim() !== "") return String(value);
        }
        return null;
    }

    isCSVFileImported() {
        return this.state.csvUrl !== null && this.state.csvHeaders !== null;
    }

    /**
     * `CSVFileInput` avisa con UN objeto nombrado, no con seis posicionales (ver el docstring de
     * `getHeadersFromCSVFileUrl`): `date_format_inference` es el séptimo dato del mismo import y
     * sumarlo como argumento 7 era pedir el bug de correrse un lugar.
     */
    handleCSVFileChange(imported) {
        const {url, headers, columnMapping, currency, timezone, dateFormat, dateFormatInference,
            sampleRows} = imported || {};
        const isEdit = this.isEditMode();
        // `sampleRows` acompaña a `csvHeaders` SIEMPRE, incluso en `null`: el preview las cruza por
        // POSICIÓN, así que dejar las viejas junto a headers nuevos muestra el valor de otra
        // columna de otro archivo — que es plausible, y por eso peor que no mostrar nada.
        const newState = {csvUrl: url, csvHeaders: headers,
                          hasImportedNewFile: true, importedSampleRows: sampleRows || null};

        // Se importó un archivo nuevo: el detalle del intento anterior ya no aplica.
        newState.submitError = null;
        newState.columnIssues = {};
        newState.diagnosticIssuesSummary = null;

        // Progressive enhancement puro: un backend viejo manda `null` acá y esto queda en null, o
        // sea que el paso 2 se comporta exactamente como antes de la inferencia.
        newState.dateFormatInference = parseDateFormatInference(dateFormatInference);

        // En edit no pisamos los valores meta (currency/timezone/date_format/name).
        if (!isEdit) {
            newState.currency = (currency !== null) ? currency : this.state.currency;
            newState.dateFormat = (dateFormat !== null) ? dateFormat : this.state.dateFormat;
            newState.timezone = (timezone !== null) ? timezone : this.state.timezone;
        }

        // El archivo NUEVO manda sobre la elección vieja. Si su inferencia es AMBIGUA, el formato que
        // había —contestado para OTRO archivo, o traído de la configuración guardada en edición— no
        // se puede dar por bueno; y mientras `dateFormat` tenga valor la pregunta NO se dibuja (ver
        // `renderDateFormatQuestion`). Sin esto, importar un `DD/MM/YYYY` ambiguo después de haber
        // contestado `MM/DD/YYYY` crea la cuenta con TODAS las fechas al revés sin preguntar nada:
        // el silencio exacto que la inferencia vino a eliminar. Vaciarlo hace que, si el usuario no
        // contesta, el submit lo NOMBRE (`sheet_account.missing.date_format`) en vez de guardar mal.
        //
        // Tres cosas que esta línea NO hace, a propósito:
        // - Con inferencia NO ambigua no toca nada: ahí el backend ya mandó `date_format` resuelto y
        //   ese gana (en el alta, por el bloque de arriba), que es como funciona hoy.
        // - Sin `date_format_inference` (backend viejo) `dateFormatInference` es null y todo se
        //   comporta como antes de la inferencia.
        // - Sólo corre en un IMPORT, que es la única entrada de este método: una elección hecha a
        //   mano sobrevive a cualquier otra interacción del formulario.
        //
        // Se ata al resultado de `parseDateFormatInference` y no al `ambiguous` crudo: si los
        // candidatos no son interpretables no hay pregunta que hacer, y vaciar el campo sin poder
        // preguntar sería cambiar un silencio por otro (el usuario se queda sin formato y sin
        // explicación). Va DESPUÉS del bloque de arriba porque en el alta el backend manda
        // `date_format: null` junto con la ambigüedad, y esa rama conservaría el formato viejo.
        if (newState.dateFormatInference) newState.dateFormat = null;

        if (columnMapping !== null && columnMapping !== undefined && !isEdit) {
            newState.columnMappings = this.state.columnMappings.map((m) => ({
                column: m.column,
                header: (columnMapping[m.column] !== undefined) ? columnMapping[m.column] : m.header
            }));
        }

        if (isEdit && headers) {
            // Si el header que tenía mapeado ya no existe en el nuevo CSV, lo limpio.
            newState.columnMappings = this.state.columnMappings.map((m) => ({
                column: m.column,
                header: (m.header !== null && headers.includes(m.header)) ? m.header : null
            }));
        }

        this.setState(newState);
    }

    handleColumnMappingChange(order, newColumn, newHeader) {
        const next = [...this.state.columnMappings];
        next[order - 1] = {column: newColumn, header: newHeader};
        // El mapeo cambió: los issues de esa columna dejan de ser confiables, y si era la columna
        // que falló, el resumen del intento anterior ya no describe el formulario que está viendo.
        const hadIssues = !!this.state.columnIssues[newColumn];
        const columnIssues = {...this.state.columnIssues};
        delete columnIssues[newColumn];
        this.setState({
            columnMappings: next,
            columnIssues: columnIssues,
            submitError: hadIssues ? null : this.state.submitError,
            diagnosticIssuesSummary: hadIssues ? null : this.state.diagnosticIssuesSummary
        });
    }

    isClientColumnInCSV() {
        return this.state.columnMappings.some(
            (m) => m.column === COLUMN_CLIENT_NAME && m.header !== null
        );
    }

    isHeaderMissing(header) {
        if (header === null || header === undefined || header === "") return false;
        if (!this.state.csvHeaders) return false;
        return !this.state.csvHeaders.includes(header);
    }

    /** Issues (ya descritos y con su sugerencia) de una columna, para anclarlos a su fila del paso 3. */
    getIssueEntriesForColumn(column) {
        const {t} = this.props;
        const issues = this.state.columnIssues[column];
        if (!issues || issues.length === 0) return {entries: [], moreText: null};

        const entries = issues.slice(0, MAX_INLINE_ISSUES).map((issue, index) => {
            const suggestion = suggestionForIssue(issue);
            return {
                key: column + '-' + index,
                text: describeIssue(issue, {t, dateFormat: this.state.dateFormat}),
                suggestion: suggestion,
                suggestionLabel: suggestion ? t('sheet_account.issues.use_date_format', {
                    format: suggestion.value, interpolation: {escapeValue: false}
                }) : null
            };
        });

        const moreText = issues.length > MAX_INLINE_ISSUES
            ? t('sheet_account.issues.more', {count: issues.length - MAX_INLINE_ISSUES})
            : null;

        return {entries, moreText};
    }

    renderColumnMappingInputs() {
        const suggestions = (this.props.diagnostic && this.props.diagnostic.remapping_suggestions) || {};
        let order = 0;
        return this.state.columnMappings.map(columnMapping => {
            order += 1;
            const {entries, moreText} = this.getIssueEntriesForColumn(columnMapping.column);
            const proposal = suggestions[columnMapping.column];
            // La propuesta deja de mostrarse en cuanto el usuario ya está donde ella apuntaba:
            // repetir "te propongo X" cuando X ya está elegido es ruido.
            const stillProposes = proposal && proposal.header && proposal.header !== columnMapping.header;
            return (
                <ColumnMappingConfiguration
                    key={columnMapping.column}
                    order={order}
                    column={columnMapping.column}
                    header={columnMapping.header}
                    headersOptions={this.state.csvHeaders}
                    headerMissing={this.isHeaderMissing(columnMapping.header)}
                    issues={entries}
                    moreIssuesText={moreText}
                    suggestedHeader={stillProposes ? proposal.header : null}
                    previousHeader={proposal ? proposal.previous_header : null}
                    headerCandidates={proposal && !proposal.header ? proposal.candidates : null}
                    preview={this.previewFor(columnMapping.header)}
                    onApplyHeaderSuggestion={this.handleApplyHeaderSuggestion}
                    onApplySuggestion={this.handleApplySuggestion}
                    onValueChange={this.handleColumnMappingChange}
                />
            );
        });
    }

    /** Cómo se leería el valor de ejemplo si el archivo estuviera en `format`. */
    describeSample(sample, format) {
        const date = readSampleAs(sample, format);
        if (!date) return format;
        // El idioma del usuario, no uno fijo: el modal existe en es y en en. Sin `i18n` (tests que
        // sólo inyectan `t`) cae al locale del runtime, que sigue siendo una fecha legible.
        const language = (this.props.i18n && this.props.i18n.language) || undefined;
        return date.toLocaleDateString(language, {day: 'numeric', month: 'long', year: 'numeric'});
    }

    /**
     * La pregunta del formato ambiguo — el caso EXACTO para el que se construyó la inferencia.
     *
     * Cuando más de un formato explica el archivo entero (`01/02/2026` es DD/MM y MM/DD a la vez),
     * el backend deja `date_format` en null a propósito y manda los candidatos. Sin esta pregunta el
     * combo queda en `-----` y el usuario elige a ciegas: el mismo silencio que la inferencia venía
     * a eliminar, y equivocarse mete TODAS las filas con la fecha cambiada.
     *
     * Dos decisiones:
     * - Las opciones dicen la FECHA, no el código (`1 de febrero de 2026`, no `DD/MM/YYYY`). Entre
     *   dos códigos no se puede elegir; entre dos fechas, sí.
     * - Desaparece apenas hay respuesta (por el botón o por el select): a partir de ahí el campo ya
     *   dice cuál quedó elegido y la pregunta sería ruido.
     *
     * ⚠️ El gate `this.state.dateFormat` es el que hace que una respuesta anterior tape la pregunta.
     * Por eso el re-import de un archivo ambiguo VACÍA el formato en `handleCSVFileChange`: la
     * respuesta vieja era sobre otro archivo y, sin vaciarlo, la pregunta no volvería a aparecer.
     */
    renderDateFormatQuestion(t) {
        const inference = this.state.dateFormatInference;
        if (!inference || this.state.dateFormat) return null;

        return (
            <div className="sheet-date-format-question" role="group"
                 aria-label={t('sheet_account.date_format_question_aria')}>
                <p className="sheet-date-format-question-title">
                    {t('sheet_account.date_format_question', {
                        sample: inference.sample, interpolation: {escapeValue: false}
                    })}
                </p>
                <div className="sheet-date-format-question-options">
                    {inference.candidates.map((format) => (
                        <button type="button"
                                key={format}
                                className="sheet-date-format-question-option"
                                onClick={() => this.handleDateFormatChange(format)}>
                            <strong>{this.describeSample(inference.sample, format)}</strong>
                            <span>{format}</span>
                        </button>
                    ))}
                </div>
                <p className="sheet-date-format-question-hint">
                    {t('sheet_account.date_format_question_hint')}
                </p>
            </div>
        );
    }

    renderSectionHead(num, titleKey, metaKey, locked, t) {
        return (
            <div className="sheet-import-section-head">
                <span className="sheet-import-section-num">{num}</span>
                <span className="sheet-import-section-title">{t(titleKey)}</span>
                <span className="sheet-import-section-meta">
                    {locked && <FaIcon iconName="lock"/>}
                    {locked ? t('sheet_account.locked_hint') : t(metaKey)}
                </span>
            </div>
        );
    }

    /** El mismo punto naranja que marca las filas obligatorias del mapeo. */
    renderRequiredDot(t) {
        return (
            <span className="sheet-required-dot" aria-label={t('sheet_account.required')}>•</span>
        );
    }

    renderSubmit(t) {
        if (this.state.isSaving) {
            return <Spinner color="positive small"/>;
        }
        const labelKey = this.isEditMode() ? 'sheet_account.submit_edit' : 'sheet_account.submit';
        // Habilitado SIEMPRE: si falta algo, el submit lo nombra. Un botón apagado sin explicación
        // es la versión educada de "no pasa nada".
        return (
            <input type="submit"
                   value={t(labelKey)}
                   className="btn -submit"/>
        );
    }

    renderSubmitError() {
        const error = this.state.submitError;
        if (!error) return null;

        return (
            <div className="sheet-submit-error" role="alert" aria-live="assertive">
                <div className="sheet-submit-error-icon">
                    <FaIcon iconName="triangle-exclamation"/>
                </div>
                <div className="sheet-submit-error-body">
                    <strong className="sheet-submit-error-title">{error.title}</strong>
                    {error.items.length > 0 && (
                        <ul className="sheet-submit-error-list">
                            {error.items.map((item, index) => <li key={index}>{item}</li>)}
                        </ul>
                    )}
                    {error.notes.map((note, index) => (
                        <p className="sheet-submit-error-note" key={index}>{note}</p>
                    ))}
                </div>
            </div>
        );
    }

    /**
     * Qué columnas tiene la planilla HOY.
     *
     * Faltaba, y era el agujero más grande de esta pantalla: decía "estas seis columnas ya no
     * existen" sin decir en ninguna parte cuáles SÍ existen. El dato siempre viajó en
     * `current_csv_headers`, pero vivía únicamente adentro de los desplegables cerrados — el
     * usuario tenía que abrir seis para enterarse de qué tenía su propio archivo.
     */
    renderAvailableHeaders() {
        const {t} = this.props;
        const headers = this.state.csvHeaders;
        if (!headers || headers.length === 0) return null;

        return (
            <p className="sheet-diagnostic-secondary">
                {t('sheet_account.diagnostic.available_headers', {
                    headers: headers.join(", "), interpolation: {escapeValue: false}
                })}
            </p>
        );
    }

    renderDiagnosticBanner() {
        const {t, diagnostic} = this.props;
        if (!this.isEditMode() || !diagnostic) return null;

        const code = diagnostic.summary_code;
        if (code === "OK") return null;

        let title;
        let bodyContent;

        if (code === "URL_NOT_ACCESSIBLE") {
            title = t('sheet_account.diagnostic.title_url');
            bodyContent = <p>{t('sheet_account.diagnostic.url_not_accessible')}</p>;
        } else if (code === "COLUMN_MAPPING_NOT_VALID") {
            title = t('sheet_account.diagnostic.title_mapping');
            const suggestions = diagnostic.remapping_suggestions || {};
            const pendingRemapping = this.pendingRemapping();
            const items = (diagnostic.missing_headers_for_mapping || []).map((m) => {
                const proposal = suggestions[m.column];
                // Cada línea dice lo que pasó Y lo que proponemos. Cuando sólo decía lo
                // primero, el usuario se quedaba con seis acusaciones y ninguna salida.
                // Pero sólo mientras la propuesta siga PENDIENTE: repetir "te proponemos X"
                // cuando X ya está elegido es el mismo ruido que las filas ya evitan.
                const key = proposal && proposal.header && pendingRemapping[m.column]
                    ? 'sheet_account.diagnostic.header_missing_with_suggestion'
                    : 'sheet_account.diagnostic.header_missing';
                return (
                    <li key={m.column}>
                        {t(key, {
                            column: resolveColumnLabel(m.column, t),
                            header: m.expected_header,
                            suggested: proposal ? proposal.header : null,
                            interpolation: {escapeValue: false}
                        })}
                    </li>
                );
            });
            const pending = Object.keys(this.pendingRemapping()).length;
            bodyContent = (
                <>
                    <p>{t('sheet_account.diagnostic.column_mapping_not_valid')}</p>
                    {items.length > 0 && <ul className="sheet-diagnostic-list">{items}</ul>}
                    {/* Las columnas que la planilla tiene hoy van ANTES del botón: es lo que el
                        usuario necesita para decidir si acepta la propuesta. Después de la acción,
                        la lectura queda invertida. */}
                    {this.renderAvailableHeaders()}
                    {pending > 0 && (
                        <button type="button"
                                className="sheet-diagnostic-apply"
                                onClick={() => this.handleApplyAllRemapping()}>
                            {t('sheet_account.remap.apply_all', {count: pending})}
                        </button>
                    )}
                </>
            );
        } else if (code === "FILTERS_NOT_VALID") {
            title = t('sheet_account.diagnostic.title_filters');
            bodyContent = <p>{t('sheet_account.diagnostic.filters_not_valid')}</p>;
        } else if (code === "ROW_DATA_NOT_VALID") {
            // La cuenta se cayó por el CONTENIDO, no por la estructura. El detalle por celda ya está
            // pintado en las filas del paso 3 (con su botón de corrección): acá va el resumen.
            // `sheet_issues` puede venir null o mal formado → banner sin detalle, nunca romper.
            title = t('sheet_account.diagnostic.title_row_data');
            const summary = this.state.diagnosticIssuesSummary;
            bodyContent = (
                <>
                    <p>{t('sheet_account.diagnostic.row_data_not_valid')}</p>
                    {summary && summary.items.length > 0 && (
                        <ul className="sheet-diagnostic-list">
                            {summary.items.map((item, index) => <li key={index}>{item}</li>)}
                        </ul>
                    )}
                    {summary && summary.notes.map((note, index) => (
                        <p className="sheet-diagnostic-secondary" key={index}>{note}</p>
                    ))}
                </>
            );
        } else {
            title = t('sheet_account.diagnostic.title_generic');
            bodyContent = <p>{t('sheet_account.diagnostic.generic')}</p>;
        }

        const duplicated = diagnostic.duplicated_headers || [];

        return (
            <div className="sheet-diagnostic-banner">
                <div className="sheet-diagnostic-icon">
                    <FaIcon iconName="triangle-exclamation"/>
                </div>
                <div className="sheet-diagnostic-body">
                    <strong className="sheet-diagnostic-title">{title}</strong>
                    {bodyContent}
                    {duplicated.length > 0 && (
                        <p className="sheet-diagnostic-secondary">
                            {t('sheet_account.diagnostic.duplicated_headers', {headers: duplicated.join(", ")})}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const isEdit = this.isEditMode();
        // El paso 1 no puede mostrar su tilde de "archivo importado" mientras el banner de
        // arriba dice que el link no responde: son dos semáforos contradictorios a 300px de
        // distancia. `url_accessible === false` es explícito para que un diagnóstico que no
        // trae la clave (o una pantalla de alta, que no tiene diagnóstico) no lo apague.
        const urlNotAccessible = !!(this.props.diagnostic && this.props.diagnostic.url_accessible === false);
        const imported = this.isCSVFileImported() && !urlNotAccessible;
        // En edit el wizard arranca con todo desbloqueado: ya hay configuración previa.
        const lockedClass = (this.isCSVFileImported() || isEdit) ? "" : " -locked";
        const fieldsLocked = isEdit; // currency/timezone/name no se editan acá
        const clientCol = this.isClientColumnInCSV();

        return (
            // `noValidate`: la validación nativa del browser NO puede enfocar un campo dentro de una
            // sección con `pointer-events: none` y bloquea el submit EN SILENCIO. La validación
            // autoritativa es `getBlockingIssues()`, que nombra lo que falta.
            <form onSubmit={this.handleSubmit} noValidate>
                <ModalBody>
                    <div className="sheet-import">
                        {this.renderDiagnosticBanner()}

                        {/* ---------- 1. Importar CSV ---------- */}
                        <div className="sheet-import-section">
                            <div className="sheet-import-section-head">
                                <span className="sheet-import-section-num">1</span>
                                <span className="sheet-import-section-title">
                                    {isEdit
                                        ? t('sheet_account.section_import_edit')
                                        : t('sheet_account.section_import')}
                                </span>
                                <span className="sheet-import-section-meta">
                                    {t('sheet_account.section_import_meta')}
                                </span>
                            </div>

                            <div className={"sheet-import-hero" + (imported ? " -imported" : "")}>
                                <div className="sheet-import-hero-icon">
                                    <FaIcon iconName={imported ? "circle-check" : "file-csv"}/>
                                </div>
                                <div className="sheet-import-hero-body">
                                    <label className="sheet-import-hero-label">
                                        {t('sheet_account.field_url')}
                                        {this.renderRequiredDot(t)}
                                        <span className="sheet-import-hero-help">
                                            <LinkToArticle article={HELP_SHEET}/>
                                        </span>
                                    </label>
                                    <CSVFileInput onImport={this.handleCSVFileChange}
                                                  initialUrl={isEdit ? this.state.csvUrl : null}
                                                  placeholder={t('sheet_account.url_placeholder')}/>
                                    {imported ? (
                                        <div className="sheet-import-hero-success" role="status" aria-live="polite">
                                            <FaIcon iconName="circle-check" className="sheet-import-hero-success-icon"/>
                                            <div className="sheet-import-hero-success-text">
                                                <strong>{t('sheet_account.imported_success_title')}</strong>
                                                <span>{t('sheet_account.imported_columns_detected', {count: this.state.csvHeaders.length})}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="sheet-import-hero-foot">
                                            <FaIcon iconName="circle-info"/>
                                            {t('sheet_account.url_help')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ---------- 2. Datos de la cuenta ---------- */}
                        <div className={"sheet-import-section" + lockedClass}>
                            {this.renderSectionHead(
                                "2",
                                'sheet_account.account_data',
                                'sheet_account.section_account_meta',
                                !imported && !isEdit,
                                t
                            )}

                            <div className="sheet-import-section-grid">
                                <div className="sheet-field">
                                    <label>{t('sheet_account.field_name')}{this.renderRequiredDot(t)}</label>
                                    <input name="account_name" type="text"
                                           value={this.state.accountName}
                                           onChange={this.handleAccountNameChange}
                                           readOnly={(!imported && !isEdit) || fieldsLocked}
                                           className="form-control"
                                           required={imported || isEdit}/>
                                </div>
                                <div className="sheet-field">
                                    <label>{t('sheet_account.field_account_id')}</label>
                                    <input name="account_id" type="number"
                                           value={this.state.accountId}
                                           onChange={this.handleAccountIdChange}
                                           readOnly={(!imported && !isEdit) || fieldsLocked}
                                           className="form-control"
                                           placeholder={t('sheet_account.auto_generate_placeholder')}/>
                                </div>
                                <div className="sheet-field">
                                    <label>{t('sheet_account.field_date_format')}{this.renderRequiredDot(t)}</label>
                                    <DateFormatSelect selected={this.state.dateFormat}
                                                      readOnly={!imported && !isEdit}
                                                      onChange={this.handleDateFormatChange}
                                                      required={imported || isEdit}/>
                                    {this.renderDateFormatQuestion(t)}
                                </div>
                                <div className="sheet-field">
                                    <label>{t('sheet_account.field_currency')}{this.renderRequiredDot(t)}</label>
                                    <CurrencySelect selected={this.state.currency}
                                                    readOnly={(!imported && !isEdit) || fieldsLocked}
                                                    onChange={this.handleCurrencyChange}
                                                    required={imported || isEdit}/>
                                </div>
                                <div className="sheet-field -full">
                                    <label>{t('sheet_account.field_timezone')}{this.renderRequiredDot(t)}</label>
                                    <TimezoneSelect selectedOption={this.state.timezone}
                                                    readOnly={(!imported && !isEdit) || fieldsLocked}
                                                    onTimezoneChange={this.handleTimezoneChange}
                                                    required={imported || isEdit}/>
                                </div>
                            </div>
                        </div>

                        {/* ---------- 3. Mapeo de columnas ---------- */}
                        <div className={"sheet-import-section" + lockedClass}>
                            {this.renderSectionHead(
                                "3",
                                'sheet_account.column_mapping',
                                'sheet_account.section_mapping_meta',
                                !imported && !isEdit,
                                t
                            )}

                            <div className="sheet-import-mapping-grid">
                                {this.renderColumnMappingInputs()}
                            </div>

                            <div className={"sheet-import-filter" + (clientCol ? "" : " -disabled")}>
                                <span className="sheet-import-filter-label">
                                    {resolveColumnLabel(COLUMN_CLIENT_NAME, t)}
                                </span>
                                <span className="sheet-import-filter-eq">=</span>
                                <input name="client_name_filter"
                                       type="text"
                                       readOnly={!clientCol}
                                       required={clientCol}
                                       placeholder={clientCol
                                           ? t('sheet_account.filter_placeholder_active')
                                           : t('sheet_account.filter_placeholder_disabled')}
                                       value={this.state.clientNameFilter}
                                       onChange={this.handleClientNameFilterChange}
                                       className="form-control"/>
                            </div>
                        </div>
                    </div>
                </ModalBody>

                {this.renderSubmitError()}

                <div className="sheet-import-footer">
                    <span className="sheet-import-footer-hint">
                        {imported || isEdit
                            ? t('sheet_account.footer_hint_ready')
                            : t('sheet_account.footer_hint_locked')}
                    </span>
                    <div className="sheet-import-footer-actions">
                        {this.renderSubmit(t)}
                    </div>
                </div>
            </form>
        )
    }

    submitUrl() {
        if (this.isEditMode()) {
            const pk = this.props.adAccountPk
                || (this.props.initialSheetAccount && this.props.initialSheetAccount.ad_account_pk);
            return API_URL + `agency/sheet-ad-account/${pk}/`;
        }
        return API_URL + 'agency/sheet-ad-account/';
    }
}

export const AddAdAccountForm = withTranslation()(AddAdAccountFormComponent);
